import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { landAxios } from '@Root/utils/landAxios'
import useAuthentication from '@Hooks/myhouse/useAuthentication'
import { IMAGE_ACCEPT_TYPE, MYHOUSE_AUTHENTICATION_CATEGORY_TYPE, MYHOUSE_AUTHENTICATION_PROGRESS, MYHOUSE_AUTHENTICATION_TYPE } from '@Root/contants/myhouseRegister'
import MyhouseAuthPackageConfirm from '@Components/atoms/MyhouseAuthPackageConfirm'
import MyhouseAuthPackage from '@Components/atoms/MyhouseAuthPackage'
import MyhouseAuthManagementFeeConfirm from '@Components/atoms/MyhouseAuthManagementFeeConfirm'
import MyhouseAuthManagementFee from '@Components/atoms/MyhouseAuthManagementFee'
import MyhouseAuthHeader from '@Components/atoms/MyhouseAuthHeader'
import MyhouseAuthVerification from '@Components/atoms/MyhouseAuthVerification'
import MyhouseAuthProgress from '@Components/atoms/MyhouseAuthProgress'
import useToast from '@Hooks/myhouse/useToast'
import { MODAL_TYPE } from '@Root/contants/myhouseModal'
import useModal from '@Hooks/myhouse/useModal'
import { AUTH_APPLY_RESPONSE, TRADE } from '@Root/contants'
import { TOAST_AFTER_EVENT, TOAST_TEXT } from '@Root/contants/myhouseToast'
import { AUTHENTICATION_IMAGE_MAX_MB_SIZE } from '@Root/contants/myhouse'
import { handleNclickMix } from '@Helpers/nclick'
import { NCLICK, NDS_PROTOCOL } from '@Root/contants/lcs'
import { lcsSend } from '@Helpers/lcs'
import useUser from '@Hooks/user/useUser'
import { getDeniedPath, LAND_DENIED_TYPE } from '@Root/contants/myhouseError'
import ResidentAuthVerificationPage from '@Components/templates/ResidentAuthVerificationPage'

type Props = {
  categoryType: MYHOUSE_AUTHENTICATION_CATEGORY_TYPE
  type: MYHOUSE_AUTHENTICATION_TYPE
  onCloseClick: (e: React.SyntheticEvent) => void
  ownerAuthCount: number
  residentAuthCount: number
}

const MyhouseAuthType: React.FC<Props> = ({ categoryType, type, onCloseClick }: Props) => {
  const history = useHistory()
  const { authentication } = useAuthentication()
  const { houseNo, tradeTypeCode, authApplyNotifyYn } = authentication
  const { open } = useModal()
  const { show, hide } = useToast()
  const { user } = useUser()
  const { goBack, length } = useHistory()
  const [progress, setProgress] = useState<MYHOUSE_AUTHENTICATION_PROGRESS>(MYHOUSE_AUTHENTICATION_PROGRESS.TERMS)
  const [file, setFile] = useState<File>({} as File)
  const [previewURL, setPreviewURL] = useState<string | ArrayBuffer>()
  const isRedirectToMain = document.referrer && !document.referrer.includes('/auth/')

  const handleResetType = (e: React.SyntheticEvent) => {
    e.preventDefault()

    if (isRedirectToMain) {
      window.location.href = `${process.env.REACT_APP_LAND_WEB_URL}/myhouse`
      return
    }

    if (length > 1) {
      goBack()
    } else {
      onCloseClick(e)
    }
  }

  const handleFileOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const reader = new FileReader()
      const file = event.target.files[0]
      if (!IMAGE_ACCEPT_TYPE.split(', ').includes(file.type)) {
        open(MODAL_TYPE.UNATTACHABLE_EXTENSION)
        return false
      }
      if (file.size > AUTHENTICATION_IMAGE_MAX_MB_SIZE * 1024 * 1024) {
        open(MODAL_TYPE.AUTH_MAX_PHOTO_SIZE)
        return false
      }
      reader.onloadend = () => {
        setFile(file)
        reader.result && setPreviewURL(reader.result)
        setProgress(MYHOUSE_AUTHENTICATION_PROGRESS.UPLOAD_CHECK)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemovePicture = (e: React.SyntheticEvent) => {
    e.preventDefault()

    setFile({} as File)
    setPreviewURL({} as string)
    setProgress(MYHOUSE_AUTHENTICATION_PROGRESS.INTRO)
    goBack()
  }

  const handleApplyFile = () => {
    applyMyhouseAuth()
  }

  const handleApplyAuth = (event: React.MouseEvent<HTMLButtonElement>, disabled: boolean) => {
    if (disabled) {
      show(TOAST_TEXT.AGREE_TERMS_OF_SERVICE)
    } else {
      if (type === MYHOUSE_AUTHENTICATION_TYPE.CERTIFICATE_OF_REALESTATE_REGISTER) {
        applyMyhouseAuth()
      } else {
        setProgress(MYHOUSE_AUTHENTICATION_PROGRESS.INTRO)
      }
    }
  }

  const applyMyhouseAuth = () => {
    const tradeType = (tradeTypeCode === TRADE.DEAL && 'DEAL') || (tradeTypeCode === TRADE.LEASE && 'LEASE') || (tradeTypeCode === TRADE.RENT && 'RENT') || 'SHORTEN'
    const formData = new FormData()
    formData.append('file', file)
    formData.append('houseNo', String(houseNo))
    formData.append('tradeType', tradeType)
    formData.append('categoryType', categoryType)
    formData.append('type', type)
    formData.append('authApplyNotifyYn', authApplyNotifyYn)
    landAxios
      .post(`/myhouse/authentication/apply`, formData)
      .then((res) => {
        if (res.data !== AUTH_APPLY_RESPONSE.AUTH_APPLY_SUCCESS) {
          history.replace(getDeniedPath(LAND_DENIED_TYPE.AUTH_APPLY_FAIL))
          return
        }
        if (categoryType === MYHOUSE_AUTHENTICATION_CATEGORY_TYPE.OWNER) {
          show('집주인 인증이 신청되었습니다\n(영업일 기준 1~3일 소요)', TOAST_AFTER_EVENT.HISTORY_PUSH, `/myhouse/${houseNo}`)
        } else {
          show('입주민 인증이 신청되었습니다\n(영업일 기준 1~3일 소요)', TOAST_AFTER_EVENT.HISTORY_PUSH, `/myhouse/${houseNo}`)
        }
      })
      .catch((e) => {
        if (categoryType === MYHOUSE_AUTHENTICATION_CATEGORY_TYPE.OWNER) {
          show('집주인 인증 신청 실패.')
        } else {
          show('입주민 인증 신청 실패.')
        }
        console.log(e)
      })
  }

  useEffect(() => {
    if (type === MYHOUSE_AUTHENTICATION_TYPE.PACKAGE && progress === MYHOUSE_AUTHENTICATION_PROGRESS.INTRO) {
      lcsSend(NDS_PROTOCOL.MY_HOUSE.AUTHENTICATION.PACKAGE)
      return
    }
    if (type === MYHOUSE_AUTHENTICATION_TYPE.PACKAGE && progress === MYHOUSE_AUTHENTICATION_PROGRESS.UPLOAD_CHECK) {
      lcsSend(NDS_PROTOCOL.MY_HOUSE.AUTHENTICATION.PACKAGE_CONFIRM)
      return
    }
    if (type === MYHOUSE_AUTHENTICATION_TYPE.MANAGEMENT_FEE && progress === MYHOUSE_AUTHENTICATION_PROGRESS.INTRO) {
      lcsSend(NDS_PROTOCOL.MY_HOUSE.AUTHENTICATION.FEE)
      return
    }
    if (type === MYHOUSE_AUTHENTICATION_TYPE.MANAGEMENT_FEE && progress === MYHOUSE_AUTHENTICATION_PROGRESS.UPLOAD_CHECK) {
      lcsSend(NDS_PROTOCOL.MY_HOUSE.AUTHENTICATION.FEE_CONFIRM)
      return
    }
    if (type === MYHOUSE_AUTHENTICATION_TYPE.CERTIFICATE_OF_REALESTATE_REGISTER && progress === MYHOUSE_AUTHENTICATION_PROGRESS.TERMS) {
      lcsSend(NDS_PROTOCOL.MY_HOUSE.AUTHENTICATION.OWNER)
      if (!user.isRealName) {
        open(MODAL_TYPE.AUTH_REALNAME_GUIDE, {
          nclickConfirm: NCLICK.MY_HOUSE.AUTHENTICATION.OWNER_REALNAME.IDENTIFICATION,
          nclickCancel: NCLICK.MY_HOUSE.AUTHENTICATION.OWNER_REALNAME.CANCEL,
        })
      }
      return
    }
    if (progress === MYHOUSE_AUTHENTICATION_PROGRESS.TERMS) {
      lcsSend(NDS_PROTOCOL.MY_HOUSE.AUTHENTICATION.RESIDENT_TERM)
      return
    }
  }, [type, progress])

  switch (type) {
    case MYHOUSE_AUTHENTICATION_TYPE.PACKAGE:
      switch (progress) {
        case MYHOUSE_AUTHENTICATION_PROGRESS.INTRO:
          return (
            <>
              <MyhouseAuthHeader
                onPrevClick={handleNclickMix(NCLICK.MY_HOUSE.AUTHENTICATION.RESIDENT_PACKAGE.BACK, handleResetType)}
                onCloseClick={handleNclickMix(NCLICK.MY_HOUSE.AUTHENTICATION.RESIDENT_PACKAGE.CLOSE, onCloseClick)}
              />
              <MyhouseAuthPackage onTakePictureChange={handleNclickMix(NCLICK.MY_HOUSE.AUTHENTICATION.RESIDENT_PACKAGE.ATTACH, handleFileOnChange)} />
            </>
          )
        case MYHOUSE_AUTHENTICATION_PROGRESS.UPLOAD_CHECK:
          return (
            <>
              <MyhouseAuthHeader
                onPrevClick={handleNclickMix(NCLICK.MY_HOUSE.AUTHENTICATION.RESIDENT_PACKAGE_CONFIRM.BACK, handleRemovePicture)}
                onCloseClick={handleNclickMix(NCLICK.MY_HOUSE.AUTHENTICATION.RESIDENT_PACKAGE_CONFIRM.CLOSE, onCloseClick)}
              />
              <MyhouseAuthPackageConfirm
                onRetakePictureChange={handleNclickMix(NCLICK.MY_HOUSE.AUTHENTICATION.RESIDENT_PACKAGE_CONFIRM.AGAIN, handleFileOnChange)}
                onUsePictureClick={handleNclickMix(NCLICK.MY_HOUSE.AUTHENTICATION.RESIDENT_PACKAGE_CONFIRM.APPLY, handleApplyFile)}
                previewURL={previewURL}
              />
            </>
          )
        case MYHOUSE_AUTHENTICATION_PROGRESS.TERMS:
          return (
            <>
              <MyhouseAuthHeader onPrevClick={handleResetType} onCloseClick={onCloseClick} />
              <MyhouseAuthVerification title={'입주민'} onAuthClick={handleApplyAuth} />
            </>
          )
      }
    case MYHOUSE_AUTHENTICATION_TYPE.MANAGEMENT_FEE:
      switch (progress) {
        case MYHOUSE_AUTHENTICATION_PROGRESS.INTRO:
          return (
            <>
              <MyhouseAuthHeader
                onPrevClick={handleNclickMix(NCLICK.MY_HOUSE.AUTHENTICATION.RESIDENT_FEE.BACK, handleResetType)}
                onCloseClick={handleNclickMix(NCLICK.MY_HOUSE.AUTHENTICATION.RESIDENT_FEE.CLOSE, onCloseClick)}
              />
              <MyhouseAuthManagementFee onTakePictureChange={handleNclickMix(NCLICK.MY_HOUSE.AUTHENTICATION.RESIDENT_FEE.ATTACH, handleFileOnChange)} />
            </>
          )
        case MYHOUSE_AUTHENTICATION_PROGRESS.UPLOAD_CHECK:
          return (
            <>
              <MyhouseAuthHeader
                onPrevClick={handleNclickMix(NCLICK.MY_HOUSE.AUTHENTICATION.RESIDENT_FEE_CONFIRM.BACK, handleRemovePicture)}
                onCloseClick={handleNclickMix(NCLICK.MY_HOUSE.AUTHENTICATION.RESIDENT_FEE_CONFIRM.CLOSE, onCloseClick)}
              />
              <MyhouseAuthManagementFeeConfirm
                onRetakePictureChange={handleNclickMix(NCLICK.MY_HOUSE.AUTHENTICATION.RESIDENT_FEE_CONFIRM.AGAIN, handleFileOnChange)}
                onUsePictureClick={handleNclickMix(NCLICK.MY_HOUSE.AUTHENTICATION.RESIDENT_FEE_CONFIRM.APPLY, handleApplyFile)}
                previewURL={previewURL}
              />
            </>
          )
        case MYHOUSE_AUTHENTICATION_PROGRESS.TERMS:
          return (
            <>
              <MyhouseAuthHeader
                onPrevClick={handleNclickMix(NCLICK.MY_HOUSE.AUTHENTICATION.RESIDENT_TERM.BACK, handleResetType)}
                onCloseClick={handleNclickMix(NCLICK.MY_HOUSE.AUTHENTICATION.RESIDENT_TERM.CLOSE, onCloseClick)}
              />
              <MyhouseAuthVerification title={'입주민'} onAuthClick={handleApplyAuth} />
            </>
          )
      }
    case MYHOUSE_AUTHENTICATION_TYPE.CERTIFICATE_OF_REALESTATE_REGISTER:
      if (!user.isRealName) {
        return null
      }
      return (
        <ResidentAuthVerificationPage
          onPrevClick={handleNclickMix(NCLICK.MY_HOUSE.AUTHENTICATION.OWNER.BACK, handleResetType)}
          onCloseClick={handleNclickMix(NCLICK.MY_HOUSE.AUTHENTICATION.OWNER.CLOSE, onCloseClick)}
          onAuthClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            handleApplyAuth(event, false)
          }}
        />
      )
    default:
      return <MyhouseAuthProgress showGuide={true} />
  }
}

export default MyhouseAuthType
